/**
 * mobile-quick-add-card
 * Bottom-sheet quick-add: slide-in/out animation + drag-to-dismiss on handle.
 * The .mqac panel is teleported to document.body on open so it always renders
 * above all stacking contexts and slides from the true bottom of the viewport.
 */
class MobileQuickAddCard extends HTMLElement {
  connectedCallback() {
    this._card     = this.querySelector('.mqac');
    this._panel    = this.querySelector('.mqac__panel');
    this._overlay  = this.querySelector('.mqac__overlay');
    this._pills    = this.querySelectorAll('.mqac__size-pill');
    this._atcBtn   = this.querySelector('.mqac__atc');
    this._varInput = this.querySelector('.mqac__variant-input');
    this._openBtn  = this.querySelector('.mqac__open-btn');
    this._handle   = this.querySelector('.mqac__handle-wrap');
    this._slides   = this.querySelectorAll('.mqac__slide');
    this._cardTrigger = this.closest('.product-upsell');

    // Placeholder keeps the card's original DOM slot so we can return it later
    this._placeholder = document.createComment('mqac-placeholder');

    // Remove .mqac from DOM initially to prevent layout issues
    if (this._card && !this._card.classList.contains('is-open')) {
      this._card.remove();
    }

    this._initSlides();
    this._initPills();
    this._initAtc();
    this._initTriggers();
    this._initDrag();
  }

  // ── Image strip ────────────────────────────────────────────────────────────

  _initSlides() {
    this._slides.forEach((slide) => {
      slide.addEventListener('click', () => {
        this._slides.forEach((s) => s.classList.remove('is-active'));
        slide.classList.add('is-active');
      });
    });
  }

  // ── Size pills ─────────────────────────────────────────────────────────────

  _initPills() {
    this._pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        if (pill.disabled) return;
        this._pills.forEach((p) => p.classList.remove('is-selected'));
        pill.classList.add('is-selected');
        if (this._varInput) this._varInput.value = pill.dataset.variantId;
        if (this._atcBtn) this._atcBtn.disabled = false;
      });
    });
  }

  // ── Add to cart ────────────────────────────────────────────────────────────

  _initAtc() {
    const form = this._atcBtn?.closest('[data-mqac-form], form');
    if (!form) return;

    const addSelectedVariant = async (e) => {
      e?.preventDefault();
      e?.stopPropagation();

      const id = this._varInput?.value;
      if (!id) return;

      this._atcBtn.classList.add('is-loading');
      this._atcBtn.disabled = true;

      const fd = new FormData(form);
      fd.set('id', id);
      fd.set('quantity', 1);

      try {
        const res  = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: fd,
        });
        const data = await res.json();
        this._atcBtn.classList.remove('is-loading');

        if (data.status) { this._atcBtn.disabled = false; return; }

        this._atcBtn.classList.add('is-added');
        setTimeout(() => { this._atcBtn.classList.remove('is-added'); this._atcBtn.disabled = false; }, 1200);

        document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { cart: data } }));
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

        this._closeCard(() => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show', { bubbles: true }));
        });
      } catch (_) {
        this._atcBtn.classList.remove('is-loading');
        this._atcBtn.disabled = false;
      }
    };

    this._atcBtn.addEventListener('click', addSelectedVariant);
    form.addEventListener('submit', addSelectedVariant);
  }

  // ── Open / close triggers ─────────────────────────────────────────────────

  _initTriggers() {
    this._openBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this._openCard();
    });

    this._cardTrigger?.addEventListener('click', (e) => {
      if (!this._isMobileViewport()) return;
      if (this._isIgnoredCardTap(e.target)) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this._openCard();
    }, true);

    this._overlay?.addEventListener('click', () => this._closeCard());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._card?.classList.contains('is-open')) this._closeCard();
    });
  }

  _isMobileViewport() {
    if (window.theme?.sizes?.small) {
      return window.theme.getWindowWidth() < window.theme.sizes.small;
    }

    return window.matchMedia('(max-width: 989px)').matches;
  }

  _isIgnoredCardTap(target) {
    return target.closest('button, a, input, select, textarea, [data-skip-upsell-product], .mqac');
  }

  _openCard() {
    this._cartWasOpen = !!document.querySelector('cart-drawer.is-open');
    this._closeCartDrawer();

    // Re-insert the card if it's not in the DOM
    if (!this._card.parentNode) {
      document.body.appendChild(this._card);
    } else {
      // Teleport .mqac to body so it's above all stacking contexts
      this._card.before(this._placeholder);
      document.body.appendChild(this._card);
    }

    // Force reflow so browser registers the translateY(100%) start state,
    // then add is-open on next frame so the transition fires
    this._card.getBoundingClientRect();
    requestAnimationFrame(() => {
      this._card.classList.add('is-open');
    });
    document.body.style.overflow = 'hidden';
    this._slides.forEach((s, i) => s.classList.toggle('is-active', i === 1));
    this._pills.forEach((p) => p.classList.remove('is-selected'));
    if (this._atcBtn) this._atcBtn.disabled = true;
    if (this._varInput) this._varInput.value = '';
  }

  _closeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer.is-open, cart-drawer.is-closing');
    if (!cartDrawer || cartDrawer.classList.contains('is-closing')) return;

    const closeButton = cartDrawer.querySelector('[data-cart-drawer-close]');
    if (closeButton) {
      closeButton.click();
      return;
    }

    cartDrawer.classList.remove('is-open');
    cartDrawer.cartDrawerIsOpen = false;
    document.dispatchEvent(new CustomEvent('theme:cart-drawer:close', { bubbles: true }));
  }

  _closeCard(onClosed) {
    this._card.classList.remove('is-open');
    document.body.style.overflow = '';
    this._pills.forEach((p) => p.classList.remove('is-selected'));
    if (this._atcBtn) this._atcBtn.disabled = true;
    if (this._varInput) this._varInput.value = '';
    this._panel.style.transform = '';
    this._overlay.style.opacity = '';

    // Wait for slide-out animation then remove .mqac from DOM
    let closeFallback = 0;
    const finishClose = () => {
      if (closeFallback) clearTimeout(closeFallback);
      this._panel.removeEventListener('transitionend', onTransitionEnd);
      
      // Remove from DOM instead of returning to placeholder
      if (this._card.parentNode) {
        this._card.parentNode.removeChild(this._card);
      }
      
      // Clean up placeholder if it exists
      if (this._placeholder.parentNode) {
        this._placeholder.parentNode.removeChild(this._placeholder);
      }
      
      if (typeof onClosed === 'function') {
        onClosed();
      } else if (this._cartWasOpen) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show', { bubbles: true }));
      }
    };
    const onTransitionEnd = () => {
      finishClose();
    };
    this._panel.addEventListener('transitionend', onTransitionEnd);
    closeFallback = setTimeout(finishClose, 450);
  }

  // ── Drag-to-dismiss on handle ─────────────────────────────────────────────

  _initDrag() {
    if (!this._handle || !this._panel) return;

    let startY = 0;
    let currentY = 0;
    let dragging = false;
    const DISMISS_THRESHOLD = 80;

    const onStart = (clientY) => {
      startY = clientY;
      currentY = 0;
      dragging = true;
      this._panel.style.transition = 'none';
      this._overlay.style.transition = 'none';
    };

    const onMove = (clientY) => {
      if (!dragging) return;
      currentY = clientY - startY;
      if (currentY < 0) currentY = 0;
      this._panel.style.transform = `translateY(${currentY}px)`;
      const panelH = this._panel.offsetHeight;
      const ratio  = Math.max(0, 1 - currentY / panelH);
      this._overlay.style.opacity = ratio * 0.45;
    };

    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      this._panel.style.transition = '';
      this._overlay.style.transition = '';

      if (currentY > DISMISS_THRESHOLD) {
        this._closeCard();
      } else {
        this._panel.style.transform = 'translateY(0)';
        this._overlay.style.opacity = '0.45';
      }
    };

    this._handle.addEventListener('touchstart', (e) => onStart(e.touches[0].clientY), { passive: true });
    this._handle.addEventListener('touchmove',  (e) => onMove(e.touches[0].clientY),  { passive: true });
    this._handle.addEventListener('touchend',   () => onEnd());

    this._handle.addEventListener('mousedown', (e) => {
      onStart(e.clientY);
      const onMM = (ev) => onMove(ev.clientY);
      const onMU = () => { onEnd(); window.removeEventListener('mousemove', onMM); window.removeEventListener('mouseup', onMU); };
      window.addEventListener('mousemove', onMM);
      window.addEventListener('mouseup', onMU);
    });

    this._handle.addEventListener('click', () => {
      if (Math.abs(currentY) < 5) this._closeCard();
    });
  }
}

customElements.define('mobile-quick-add-card', MobileQuickAddCard);
